import { useEffect, useRef } from "react";
import { Form, Space } from "antd";

import { W1 } from "@components/Constants";
import { Stack } from "@components/Page";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";
import { MONTHLY as DISTRIBUTION_FREQUENCY_MONTHLY } from "@api/services/transactions/shapes/DistributionFrequencyEnum";

import useDelete from "./helpers/useDelete";
import useDisable from "./helpers/useDisable";
import useFinish from "./helpers/useFinish";
import getNextDate from "./helpers/getNextDate";
import getFormFields from "./helpers/getFormFields";
import useEnable from "./helpers/useEnable";
import DistributionsScheduleSummary from "../../DistributionsScheduleSummary";
import type { Investment, ScheduleFormValues } from "../types";
import BeneficiariesTable, { type BeneficiariesTableHandle } from "../BeneficiariesTable";
import ScheduleName from "./ScheduleName";

type Props = {
  scheduleId?: string;
  investments: Investment[];
  initialValues: ScheduleFormValues;
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  deleteSchedule: (scheduleId: string) => Promise<unknown>;
  enableSchedule: (scheduleId: string) => Promise<unknown>;
  disableSchedule: (scheduleId: string) => Promise<unknown>;
  onClose: () => void;
}


const DistributionsScheduleForm = ({
  scheduleId = undefined,
  onClose,
  investments,
  initialValues,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  enableSchedule,
  disableSchedule,
}: Props) => {
  const [ form ] = Form.useForm<ScheduleFormValues>();

  const beneficiariesTableRef = useRef<BeneficiariesTableHandle>(null);

  const startDate = Form.useWatch("startDate", form);
  const shouldRoundCents = Form.useWatch("shouldRoundCents", form);
  const distributionFrequency = Form.useWatch("distributionFrequency", form);

  const nextDate = getNextDate(startDate, distributionFrequency);

  const { onFinish, isLoading: isUpdating } = useFinish({ beneficiariesTableRef, scheduleId, createSchedule, updateSchedule, onClose });
  
  const { onDeleteSchedule, isLoading: isDeleting } = useDelete({ scheduleId, deleteSchedule, onClose });
  const { onEnableSchedule, isLoading: isEnabling } = useEnable({ scheduleId, enableSchedule });
  const { onDisableSchedule, isLoading: isDisabling } = useDisable({ scheduleId, disableSchedule });
  
  const isLoading = isUpdating || isEnabling || isDisabling || isDeleting;
  const scheduleStatus = initialValues.status;

  useEffect(() => {
    form.setFieldValue("nextDate", nextDate);
  }, [form, nextDate]);

  const formProps = {
    layout: "inline" as const,
    initialValues,
    form,
    onFinish,
  };

  const formFields = getFormFields({
    isUpdate: !!scheduleId,
    onDelete: onDeleteSchedule,
    isLoading,
    scheduleStatus,
    onEnable: onEnableSchedule,
    onDisable: onDisableSchedule,
  });

  const beneficiariesTableProps = {
    ref: beneficiariesTableRef,
    investments,
    shouldRoundCents: shouldRoundCents ?? initialValues.shouldRoundCents ?? false,
    initialDistributions: initialValues.distributions,
    distributionFrequency: distributionFrequency ?? initialValues.distributionFrequency ?? DISTRIBUTION_FREQUENCY_MONTHLY,
  };

  return (
    <Stack>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Space size={W1}>
          <ScheduleName initialValues={initialValues} />
          <DistributionsScheduleSummary nextDate={nextDate} status={scheduleStatus} />
        </Space>

        <Form {...formProps}>
          {
            formFields.map(({ key, itemProps, children }) => (
              <Form.Item key={key} {...itemProps}>
                {children}
              </Form.Item>
            ))
          }
        </Form>
      </div>

      <BeneficiariesTable {...beneficiariesTableProps} />
    </Stack>
  );
};

export default DistributionsScheduleForm;
